import { useCallback, useEffect, useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Floating } from "./floating";
import { TextInput, useFieldControl } from "./controls";
import { Icon, type IconName } from "./icons";

/* ---------------------------------------------------------------- date */

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const monthTitle = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });
const dayName = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
const triggerLabel = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });

function midnight(ts: number): number {
  const date = new Date(ts);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function addDays(ts: number, days: number): number {
  const date = new Date(ts);
  date.setDate(date.getDate() + days);
  return date.getTime();
}

function addMonths(ts: number, months: number): number {
  const date = new Date(ts);
  const day = date.getDate();
  date.setDate(1);
  date.setMonth(date.getMonth() + months);
  date.setDate(Math.min(day, new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()));
  return date.getTime();
}

/** Six Monday-first weeks covering the month that contains `ts`. */
function monthGrid(ts: number): number[] {
  const first = new Date(ts);
  first.setDate(1);
  first.setHours(0, 0, 0, 0);
  const start = addDays(first.getTime(), -((first.getDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
}

const sameMonth = (a: number, b: number) => new Date(a).getMonth() === new Date(b).getMonth() && new Date(a).getFullYear() === new Date(b).getFullYear();

/** A date button that opens a month calendar. `value` is local midnight of the chosen day. */
export function DateField({ value, onChange, max, id, className = "", "aria-label": ariaLabel }: {
  value: number | null;
  onChange: (day: number) => void;
  /** Latest pickable day, as any timestamp on that day. */
  max?: number;
  id?: string;
  className?: string;
  "aria-label"?: string;
}) {
  const control = useFieldControl(id);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const floatingRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(() => midnight(value ?? Date.now()));
  const [direction, setDirection] = useState(0);
  const today = midnight(Date.now());
  const limit = max === undefined ? Infinity : midnight(max);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus({ preventScroll: true });
  }, []);
  const dismiss = useCallback(() => close(false), [close]);

  useEffect(() => {
    if (open) gridRef.current?.querySelector<HTMLElement>(`[data-day="${focus}"]`)?.focus({ preventScroll: true });
  }, [open, focus]);

  const moveTo = (next: number) => {
    const day = midnight(next);
    if (!sameMonth(day, focus)) setDirection(day > focus ? 1 : -1);
    setFocus(day);
  };

  const pick = (day: number) => {
    if (day > limit) return;
    onChange(day);
    close();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const keys: Record<string, () => number> = {
      ArrowLeft: () => addDays(focus, -1),
      ArrowRight: () => addDays(focus, 1),
      ArrowUp: () => addDays(focus, -7),
      ArrowDown: () => addDays(focus, 7),
      PageUp: () => addMonths(focus, -1),
      PageDown: () => addMonths(focus, 1),
      Home: () => addDays(focus, -((new Date(focus).getDay() + 6) % 7)),
      End: () => addDays(focus, 6 - ((new Date(focus).getDay() + 6) % 7))
    };
    if (keys[event.key]) {
      event.preventDefault();
      moveTo(keys[event.key]!());
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === "Tab") {
      event.preventDefault();
      close();
    }
  };

  const days = monthGrid(focus);

  return <>
    <button
      ref={triggerRef}
      type="button"
      id={control.id}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={ariaLabel ? `${ariaLabel}, ${value === null ? "not set" : dayName.format(value)}` : undefined}
      aria-describedby={control["aria-describedby"]}
      className={`ot-select ot-select--md ot-select--field ${open ? "is-open" : ""} ${className}`.trim()}
      onClick={() => {
        if (open) return close();
        setFocus(midnight(value ?? Date.now()));
        setDirection(0);
        setOpen(true);
      }}
    >
      <Icon name="calendar" className="ot-select__icon"/>
      <span className={`ot-select__value ${value === null ? "is-placeholder" : ""}`}>
        {value === null ? "Pick a day" : value === today ? <>Today<span className="ot-select__hint">{triggerLabel.format(value)}</span></> : value === addDays(today, -1) ? <>Yesterday<span className="ot-select__hint">{triggerLabel.format(value)}</span></> : triggerLabel.format(value)}
      </span>
      <Icon name="caret-down" weight="bold" className="ot-select__caret"/>
    </button>
    <Floating open={open} anchorRef={triggerRef} floatingRef={floatingRef} onDismiss={dismiss} className="ot-calendar-panel" role="dialog" aria-label="Choose a day">
      <div className="ot-calendar" onKeyDown={onKeyDown}>
        <div className="ot-calendar__head">
          <button type="button" className="ot-calendar__nav" aria-label="Previous month" onClick={() => moveTo(addMonths(focus, -1))}><Icon name="caret-left" weight="bold"/></button>
          <AnimatePresence mode="popLayout" initial={false} custom={direction}>
            <motion.strong
              key={monthTitle.format(focus)}
              className="ot-calendar__title"
              aria-live="polite"
              initial={{ opacity: 0, x: direction * 14 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction * -14 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            >{monthTitle.format(focus)}</motion.strong>
          </AnimatePresence>
          <button type="button" className="ot-calendar__nav" aria-label="Next month" onClick={() => moveTo(addMonths(focus, 1))} disabled={addMonths(focus, 1) > limit && sameMonth(focus, limit)}><Icon name="caret-right" weight="bold"/></button>
        </div>
        <div className="ot-calendar__weekdays" aria-hidden="true">{WEEKDAYS.map((day) => <span key={day}>{day}</span>)}</div>
        <div className="ot-calendar__grid" ref={gridRef} role="grid" aria-label={monthTitle.format(focus)}>
          {days.map((day) => {
            const outside = !sameMonth(day, focus);
            const selected = value !== null && day === midnight(value);
            const disabled = day > limit;
            return <button
              key={day}
              type="button"
              role="gridcell"
              data-day={day}
              tabIndex={day === focus ? 0 : -1}
              aria-selected={selected}
              aria-current={day === today ? "date" : undefined}
              aria-label={dayName.format(day)}
              aria-disabled={disabled || undefined}
              className={`ot-calendar__day ${outside ? "is-outside" : ""} ${selected ? "is-selected" : ""} ${day === today ? "is-today" : ""} ${disabled ? "is-disabled" : ""}`}
              onClick={() => pick(day)}
            >{new Date(day).getDate()}</button>;
          })}
        </div>
        <div className="ot-calendar__foot">
          <button type="button" onClick={() => pick(today)} disabled={today > limit}>Today</button>
          <button type="button" onClick={() => pick(addDays(today, -1))}>Yesterday</button>
        </div>
      </div>
    </Floating>
  </>;
}

/* ---------------------------------------------------------------- time */

const pad = (value: number) => String(value).padStart(2, "0");

/** Reads "9", "930", "9:30", "9.30", "21h", "9pm", "9:30 am" as "HH:MM". Null when unreadable. */
export function parseTime(text: string): string | null {
  const match = /^(\d{1,2})(?:[:.h]?(\d{2}))?\s*(am|pm|a|p)?$/i.exec(text.trim().replace(/\s+/g, " "));
  if (!match) {
    const compact = /^(\d{3,4})$/.exec(text.trim());
    if (!compact) return null;
    return parseTime(`${compact[1]!.slice(0, -2)}:${compact[1]!.slice(-2)}`);
  }
  let hours = Number(match[1]);
  const minutes = match[2] ? Number(match[2]) : 0;
  const meridiem = match[3]?.toLowerCase()[0];
  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    hours = (hours % 12) + (meridiem === "p" ? 12 : 0);
  }
  if (hours > 23 || minutes > 59) return null;
  return `${pad(hours)}:${pad(minutes)}`;
}

function shiftTime(value: string, minutes: number): string {
  const parsed = parseTime(value) ?? "00:00";
  const [h, m] = parsed.split(":").map(Number) as [number, number];
  const total = (((h * 60 + m + minutes) % 1440) + 1440) % 1440;
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}

/**
 * A forgiving time input: type "930" or "9:30pm" and it settles on "21:30" when you leave it.
 * Arrow keys nudge by a minute, or a quarter hour with Shift.
 */
export function TimeField({ value, onChange, placeholder = "09:00", id, "aria-label": ariaLabel, invalid }: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  invalid?: boolean;
  "aria-label"?: string;
}) {
  const [draft, setDraft] = useState(value);
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setDraft(value); }, [value]);

  const settle = (text: string) => {
    const parsed = text.trim() ? parseTime(text) : "";
    const next = parsed ?? text;
    setDraft(next);
    if (next !== value) onChange(next);
  };

  return <TextInput
    id={id}
    aria-label={ariaLabel}
    className="ot-time"
    inputMode="numeric"
    autoComplete="off"
    leading={<Icon name="clock"/>}
    placeholder={placeholder}
    value={draft}
    invalid={invalid || (draft.trim() !== "" && parseTime(draft) === null && !focused.current)}
    onFocus={(event) => { focused.current = true; event.currentTarget.select(); }}
    onChange={(event) => setDraft(event.target.value)}
    onBlur={() => { focused.current = false; settle(draft); }}
    onKeyDown={(event) => {
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        const next = shiftTime(draft || placeholder, (event.key === "ArrowUp" ? 1 : -1) * (event.shiftKey ? 15 : 1));
        setDraft(next);
        onChange(next);
      } else if (event.key === "Enter") {
        settle(draft);
      }
    }}
  />;
}

/* ---------------------------------------------------------------- files */

/** A drop zone that is also a file button: drag a file onto it, or click / press Enter to browse. */
export function FileDrop({ accept, onFile, title, hint, icon = "upload", busy = false, disabled = false, className = "", children }: {
  accept: string;
  onFile: (file: File) => void;
  title: ReactNode;
  hint?: ReactNode;
  icon?: IconName;
  busy?: boolean;
  disabled?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const take = (file: File | undefined) => { if (file && !disabled && !busy) onFile(file); };

  const drag = {
    onDragEnter: (event: DragEvent) => { event.preventDefault(); depth.current += 1; setOver(true); },
    onDragOver: (event: DragEvent) => { event.preventDefault(); event.dataTransfer.dropEffect = "copy"; },
    onDragLeave: () => { depth.current = Math.max(0, depth.current - 1); if (!depth.current) setOver(false); },
    onDrop: (event: DragEvent) => {
      event.preventDefault();
      depth.current = 0;
      setOver(false);
      take(event.dataTransfer.files[0]);
    }
  };

  return <label className={`ot-drop ${over ? "is-over" : ""} ${busy ? "is-busy" : ""} ${disabled ? "is-disabled" : ""} ${className}`.trim()} {...drag}>
    <input type="file" accept={accept} disabled={disabled || busy} onChange={(event) => { take(event.target.files?.[0]); event.target.value = ""; }}/>
    <motion.span className="ot-drop__icon" animate={over ? { y: -3, scale: 1.08 } : { y: 0, scale: 1 }} transition={{ type: "spring", stiffness: 500, damping: 26 }}>
      <Icon name={icon} weight="bold"/>
    </motion.span>
    <span className="ot-drop__text">
      <strong>{busy ? "Reading…" : over ? "Drop to use this file" : title}</strong>
      {hint && <span>{hint}</span>}
    </span>
    {children}
  </label>;
}

